"use client";

import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Lock from "@churchofjesuschrist/eden-body-lock";
import { grey2, black, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import Card from "@churchofjesuschrist/eden-card";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import { mediaBreak480w } from "@churchofjesuschrist/eden-styled-helpers";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [platformModalBackdropBackgroundDef, platformModalBackdropBackground] = setAppearanceConstantLocal("alert-modal-backdrop-background", {
  light: adjustHexAlpha(grey2, 0.6),
  dark: adjustHexAlpha(black, 0.6),
  prefix: "eden"
});
const Dialog = styled(Card).withConfig({
  componentId: "sc-7cm3zw-0"
})(["display:flex;flex-direction:column;visibility:hidden;overflow:visible;padding:0;color:inherit;width:100vw;max-width:100dvw;max-height:100dvh;&[open]{visibility:visible;}&::backdrop{", ";backdrop-filter:blur(2px);background:", ";}", ";"], platformModalBackdropBackgroundDef, platformModalBackdropBackground, mediaBreak480w`
    width: fit-content;
    max-width: 34.375rem;
    min-width: 25rem;
    max-height: calc(100vh - 6rem);
  `);

// This is to suppress warnings related to not being able to use the `autoFocus` prop due to this bug: https://github.com/facebook/react/issues/23301
if (typeof console !== "undefined" && typeof console.error === "function") {
  const consoleError = console.error;
  console.error = function filterWarnings(...args) {
    if (!args.includes("autoFocus")) {
      consoleError(...args);
    }
  };
}
const PlatformModal = ({
  children,
  open,
  onClose,
  ...props
}) => {
  const dialog = useRef();
  useEffect(() => {
    if (open) {
      dialog.current.returnValue = "";
      dialog.current.showModal?.();
    } else {
      //Passed in string updates the returnValue of the dialog. Used to detect if close is coming from open prop change.
      dialog.current.close?.("eden-platform-modal-closed");
    }
  }, [open, dialog]);
  const handleDialogClick = event => {
    if (event.target === event.currentTarget) {
      handleClose(event);
    }
  };
  const handleClose = event => {
    if (dialog.current.returnValue !== "eden-platform-modal-closed") {
      event.preventDefault(); // Prevents the default action from native close events so that control is left up to the `open` prop.
      onClose(event);
    }
  };
  return /*#__PURE__*/_jsxs(Dialog, {
    ref: dialog,
    onClick: handleDialogClick,
    onClose: handleClose,
    onCancel: handleClose,
    renderAs: "dialog",
    ...props,
    children: [/*#__PURE__*/_jsx(Lock, {
      active: open
    }), children]
  });
};
PlatformModal.propTypes = {
  /** Modal content */
  children: PropTypes.node,
  /** Whether modal is open */
  open: PropTypes.bool,
  /** Callback to execute when a close method is triggered */
  onClose: PropTypes.func.isRequired
};
export default PlatformModal;